import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Me, UpdateUser } from "../api/users";
import { RestaurantGet, GetAcativeRestaurants, GetRestaurants } from "../api/restaurant";
import { TYLevel } from "../constants";

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  return fallback;
}

const Header = ({ isAuthenticated, userAccessLevel, token }) => {
  const canSwitch = isAuthenticated && Number(userAccessLevel) >= TYLevel;
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [userId, setUserId] = useState(null);
  const [currentRestaurantId, setCurrentRestaurantId] = useState(0);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(0);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (!canSwitch || !token) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoadingList(true);
      try {
        const me = await Me(token);
        const current = await RestaurantGet(token, me.restaurant_id);
        let list = await GetAcativeRestaurants(token);
        if (!Array.isArray(list) || list.length === 0) {
          const paged = await GetRestaurants(token, 1, 200);
          list = Array.isArray(paged?.data) ? paged.data : [];
        }
        if (cancelled) return;
        const active = list
          .filter((item) => item?.is_active !== false)
          .sort((a, b) => String(a.name).localeCompare(String(b.name), "ru"));
        setUserId(me.id);
        setCurrentRestaurantId(Number(current.id));
        setSelectedRestaurantId(Number(current.id));
        setRestaurantName(current.name || "");
        setRestaurants(active);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [canSwitch, token]);

  const filteredRestaurants = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return restaurants;
    return restaurants.filter((item) =>
      `${item.name ?? ""} ${item.bk_name ?? ""} ${item.c_name ?? ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [restaurants, query]);

  const openModal = () => {
    setSelectedRestaurantId(currentRestaurantId);
    setQuery("");
    setMessage(null);
  };

  const saveRestaurant = async () => {
    if (!userId) return;
    if (!Number(selectedRestaurantId)) {
      setMessage({ ok: false, text: "Выберите ресторан" });
      return;
    }
    if (Number(selectedRestaurantId) === Number(currentRestaurantId)) {
      setMessage({ ok: false, text: "Этот ресторан уже выбран" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const data = await UpdateUser(token, {
        id: userId,
        restaurant_id: Number(selectedRestaurantId),
      });
      if (data?.detail) {
        setMessage({ ok: false, text: errorText(data, "Не удалось сменить ресторан") });
        return;
      }
      const next = restaurants.find(
        (item) => Number(item.id) === Number(selectedRestaurantId),
      );
      setRestaurantName(next?.name || restaurantName);
      setCurrentRestaurantId(Number(selectedRestaurantId));
      setMessage({ ok: true, text: "Ресторан сменён, обновляем страницу…" });
      window.setTimeout(() => window.location.reload(), 450);
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Не удалось сменить ресторан" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="app-header">
      <Link to="/" className="app-header-brand">
        <span className="app-header-mark">S</span>
        Schedule
      </Link>

      {canSwitch && (
        <button
          type="button"
          className="app-header-restaurant"
          data-bs-toggle="modal"
          data-bs-target="#staticBackdropChangeRest"
          onClick={openModal}
        >
          <span>Ресторан</span>
          <strong>{restaurantName || (loadingList ? "Загрузка…" : "Не выбран")}</strong>
        </button>
      )}

      <div
        className="modal fade"
        id="staticBackdropChangeRest"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabelChangeRest"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content app-rest-modal">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title" id="staticBackdropLabelChangeRest">
                  Сменить ресторан
                </h5>
                <p className="app-rest-modal-current">
                  Сейчас: <b>{restaurantName || "не выбран"}</b>
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Закрыть"
              />
            </div>
            <div className="modal-body pt-2">
              <input
                type="search"
                className="form-control"
                placeholder="Поиск по названию"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
              />
              <div className="app-rest-list" role="listbox" aria-label="Рестораны">
                {loadingList ? (
                  <p className="app-rest-empty">Загрузка списка…</p>
                ) : filteredRestaurants.length === 0 ? (
                  <p className="app-rest-empty">Ничего не найдено</p>
                ) : (
                  filteredRestaurants.map((item) => {
                    const selected =
                      Number(item.id) === Number(selectedRestaurantId);
                    const current =
                      Number(item.id) === Number(currentRestaurantId);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`app-rest-item ${selected ? "is-selected" : ""}`}
                        onClick={() => {
                          setSelectedRestaurantId(Number(item.id));
                          setMessage(null);
                        }}
                      >
                        <span className="app-rest-item-name">{item.name}</span>
                        {current ? <em>сейчас</em> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              {message ? (
                <span className={message.ok ? "is-ok" : "is-error"}>
                  {message.text}
                </span>
              ) : (
                <span className="app-rest-hint">
                  После сохранения страница обновится
                </span>
              )}
              <button
                type="button"
                className="btn"
                onClick={saveRestaurant}
                disabled={
                  saving ||
                  loadingList ||
                  Number(selectedRestaurantId) === Number(currentRestaurantId)
                }
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
